import {useEffect, useId, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {X} from "lucide-react";
import {mapApiError} from "../../../shared/api/errors";
import {useTranslation} from "../../../shared/i18n";
import {MutationResultBanner} from "../../../shared/components/MutationResultBanner";
import {createCategory, createCity, loadPropertyCountries} from "../api";

const focusableElements = (container) =>
    Array.from(
        container.querySelectorAll(
            "button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex='-1'])",
        ),
    );

const positiveCountryId = (value) => {
    const normalized = String(value ?? "").trim();
    return /^[1-9]\d*$/.test(normalized) ? normalized : "";
};

const dialogCopy = (kind, t) => kind === "category"
    ? {
        title: t("orgs.property.categoryTitle"),
        description: t("orgs.property.categoryCopy"),
        submit: t("orgs.property.categorySubmit"),
        success: t("orgs.property.categorySuccess"),
    }
    : {
        title: t("orgs.property.cityTitle"),
        description: t("orgs.property.cityCopy"),
        submit: t("orgs.property.citySubmit"),
        success: t("orgs.property.citySuccess"),
    };

export const PropertyCreationDialog = ({open, kind, countryScope, onCancel}) => {
    const {t} = useTranslation();
    const titleId = useId();
    const descriptionId = useId();
    const dialogRef = useRef(null);
    const initialFocusRef = useRef(null);
    const invokingElementRef = useRef(null);
    const queryClient = useQueryClient();
    const [countryId, setCountryId] = useState("");
    const [cityName, setCityName] = useState("");
    const [categoryName, setCategoryName] = useState("");
    const [subCategoryName, setSubCategoryName] = useState("");
    const [validationError, setValidationError] = useState("");
    const [result, setResult] = useState(null);
    const copy = dialogCopy(kind, t);

    const countriesQuery = useQuery({
        queryKey: ["admin-property-countries"],
        queryFn: loadPropertyCountries,
        enabled: open && kind === "city",
        retry: false,
    });

    useEffect(() => {
        if (!open) return;
        setCountryId("");
        setCityName("");
        setCategoryName("");
        setSubCategoryName("");
        setValidationError("");
        setResult(null);
    }, [kind, open]);

    useEffect(() => {
        if (!open || kind !== "city" || countryId) return;
        const scopedCountryId = positiveCountryId(countryScope);
        if (scopedCountryId && (countriesQuery.data || []).some(
            (country) => String(country.id) === scopedCountryId,
        )) {
            setCountryId(scopedCountryId);
        }
    }, [countriesQuery.data, countryId, countryScope, kind, open]);

    const mutation = useMutation({
        mutationFn: () => {
            if (kind === "category") {
                return createCategory({
                    category_name: categoryName.trim(),
                    sub_category_name: subCategoryName.trim(),
                });
            }
            const country = (countriesQuery.data || []).find(
                (item) => String(item.id) === String(countryId),
            );
            return createCity({
                country_name: country.name,
                city_name: cityName.trim(),
            });
        },
        onSuccess: (response) => {
            void queryClient.invalidateQueries({queryKey: ["admin-place-properties"]});
            setResult({kind: "success", message: response?.message || copy.success});
            setValidationError("");
        },
        onError: (error) => {
            setResult({kind: "error", message: mapApiError(error).message});
        },
    });

    useEffect(() => {
        if (!open) return undefined;
        invokingElementRef.current = document.activeElement;
        const frame = window.requestAnimationFrame(() => {
            const dialog = dialogRef.current;
            if (!dialog) return;
            const initialFocus = initialFocusRef.current;
            if (initialFocus && !initialFocus.disabled) {
                initialFocus.focus();
                return;
            }
            (focusableElements(dialog)[0] || dialog).focus();
        });
        return () => {
            window.cancelAnimationFrame(frame);
            const invokingElement = invokingElementRef.current;
            if (invokingElement && document.contains(invokingElement)) invokingElement.focus();
            invokingElementRef.current = null;
        };
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                if (mutation.isPending) {
                    event.preventDefault();
                } else {
                    onCancel();
                }
                return;
            }
            if (event.key !== "Tab" || !dialogRef.current) return;
            const dialog = dialogRef.current;
            const focusable = focusableElements(dialog);
            if (focusable.length === 0) {
                event.preventDefault();
                dialog.focus();
                return;
            }
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (!dialog.contains(document.activeElement)) {
                event.preventDefault();
                (event.shiftKey ? last : first).focus();
            } else if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [mutation.isPending, onCancel, open]);

    if (!open) return null;

    const handleSubmit = (event) => {
        event.preventDefault();
        setResult(null);
        if (kind === "city") {
            if (!countryId) {
                setValidationError(t("orgs.property.pickCountry"));
                return;
            }
            if (!cityName.trim()) {
                setValidationError(t("orgs.property.cityNameRequired"));
                return;
            }
        } else if (!categoryName.trim() || !subCategoryName.trim()) {
            setValidationError(t("orgs.property.namesRequired"));
            return;
        }
        setValidationError("");
        mutation.mutate();
    };

    return createPortal(
        <div
            className="finance-dialog-backdrop"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget && !mutation.isPending) onCancel();
            }}
        >
            <section
                ref={dialogRef}
                className="finance-dialog organization-property-dialog"
                role="dialog"
                aria-modal="true"
                aria-busy={mutation.isPending}
                aria-labelledby={titleId}
                aria-describedby={descriptionId}
                tabIndex={-1}
            >
                <form onSubmit={handleSubmit}>
                    <header className="finance-dialog-header">
                        <div>
                            <h2 id={titleId}>{copy.title}</h2>
                            <p id={descriptionId}>{copy.description}</p>
                        </div>
                        <button
                            className="finance-icon-button"
                            type="button"
                            aria-label={t("orgs.common.close")}
                            title={t("orgs.common.close")}
                            disabled={mutation.isPending}
                            onClick={onCancel}
                        >
                            <X aria-hidden="true" size={18} />
                        </button>
                    </header>

                    <div className="finance-dialog-fields">
                        {kind === "city" ? (
                            <>
                                <label>
                                    <span>{t("orgs.property.country")}</span>
                                    <select
                                        ref={initialFocusRef}
                                        aria-label={t("orgs.property.country")}
                                        value={countryId}
                                        disabled={countriesQuery.isLoading || mutation.isPending}
                                        onChange={(event) => {
                                            setCountryId(event.target.value);
                                            setValidationError("");
                                            setResult(null);
                                        }}
                                    >
                                        <option value="">{t("orgs.property.chooseCountry")}</option>
                                        {(countriesQuery.data || []).map((country) => (
                                            <option key={country.id} value={country.id}>{country.name}</option>
                                        ))}
                                    </select>
                                </label>
                                <label>
                                    <span>{t("orgs.property.cityName")}</span>
                                    <input
                                        aria-label={t("orgs.property.cityName")}
                                        value={cityName}
                                        disabled={mutation.isPending}
                                        onChange={(event) => {
                                            setCityName(event.target.value);
                                            setValidationError("");
                                            setResult(null);
                                        }}
                                    />
                                </label>
                            </>
                        ) : (
                            <>
                                <label>
                                    <span>{t("orgs.property.categoryName")}</span>
                                    <input
                                        ref={initialFocusRef}
                                        aria-label={t("orgs.property.categoryName")}
                                        value={categoryName}
                                        disabled={mutation.isPending}
                                        onChange={(event) => {
                                            setCategoryName(event.target.value);
                                            setValidationError("");
                                            setResult(null);
                                        }}
                                    />
                                </label>
                                <label>
                                    <span>{t("orgs.property.subcategoryName")}</span>
                                    <input
                                        aria-label={t("orgs.property.subcategoryName")}
                                        value={subCategoryName}
                                        disabled={mutation.isPending}
                                        onChange={(event) => {
                                            setSubCategoryName(event.target.value);
                                            setValidationError("");
                                            setResult(null);
                                        }}
                                    />
                                </label>
                            </>
                        )}
                    </div>

                    {countriesQuery.isError ? (
                        <div className="finance-dialog-error" role="alert">{t("orgs.property.countriesError")}</div>
                    ) : null}
                    {validationError ? (
                        <div className="finance-dialog-error" role="alert">{validationError}</div>
                    ) : null}
                    <MutationResultBanner result={result} />

                    <footer className="finance-dialog-footer">
                        <button
                            type="button"
                            className="admin-button admin-button-secondary"
                            disabled={mutation.isPending}
                            onClick={onCancel}
                        >
                            {t("orgs.common.cancel")}
                        </button>
                        <button
                            type="submit"
                            className="admin-button admin-button-primary"
                            disabled={mutation.isPending || countriesQuery.isError}
                        >
                            {mutation.isPending ? t("orgs.common.savingShort") : copy.submit}
                        </button>
                    </footer>
                </form>
            </section>
        </div>,
        document.body,
    );
};
